import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Play, Instagram, Youtube, Image as ImageIcon, X } from "lucide-react";

const TABS = [
  { key: "all", label: "All", icon: ImageIcon },
  { key: "instagram_post", label: "Instagram Posts", icon: Instagram },
  { key: "instagram_reel", label: "Instagram Reels", icon: Instagram },
  { key: "youtube_video", label: "YouTube Videos", icon: Youtube },
  { key: "photo", label: "Photos", icon: ImageIcon },
];

function toEmbedUrl(url) {
  if (!url) return "";
  if (url.includes("youtu.be/")) return `https://www.youtube.com/embed/${url.split("youtu.be/")[1].split(/[?&]/)[0]}`;
  if (url.includes("youtube.com/watch")) { try { return `https://www.youtube.com/embed/${new URL(url).searchParams.get("v")}`; } catch { return url; } }
  if (url.includes("instagram.com/reel") || url.includes("instagram.com/p/")) return url + "embed/";
  return url;
}

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/gallery").then(({ data }) => setItems(data)); }, []);

  const filtered = tab === "all" ? items : items.filter((i) => (i.type || "photo") === tab);

  return (
    <div className="pt-12 pb-24">
      <div className="container-eq">
        <div className="max-w-3xl">
          <div className="label-eq text-sky-500 mb-3">Gallery</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter" data-testid="gallery-title">Life at EQtech Academy</h1>
          <p className="mt-4 text-neutral-600">Peek into our classrooms, projects, workshops, and Instagram community.</p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2 border-b border-neutral-200 pb-2" data-testid="gallery-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${tab === t.key ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100"}`}
              data-testid={`gallery-tab-${t.key}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
          {filtered.map((g) => {
            const isVideo = g.type === "youtube_video" || g.type === "instagram_reel";
            return (
              <button key={g.id} onClick={() => setActive(g)} className="relative aspect-square rounded-2xl overflow-hidden bg-neutral-100 group text-left" data-testid={`gallery-item-${g.id}`}>
                <img src={g.image} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {isVideo && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-6 h-6 text-neutral-900 fill-neutral-900 translate-x-0.5" />
                    </div>
                  </div>
                )}
                {g.type === "instagram_post" && <Instagram className="absolute top-2 right-2 w-4 h-4 text-white drop-shadow" />}
                {g.type === "instagram_reel" && <Instagram className="absolute top-2 right-2 w-4 h-4 text-white drop-shadow" />}
                {g.type === "youtube_video" && <Youtube className="absolute top-2 right-2 w-4 h-4 text-white drop-shadow" />}
                {g.caption && (
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white text-xs">
                    <div className="line-clamp-2">{g.caption}</div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {filtered.length === 0 && <div className="text-center text-neutral-500 py-16">No items in this category yet.</div>}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="gallery-modal">
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setActive(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"><X className="w-5 h-5" /></button>
            {(active.type === "youtube_video" || active.type === "instagram_reel") && active.video_url ? (
              <div className="rounded-2xl overflow-hidden aspect-video bg-black">
                <iframe src={toEmbedUrl(active.video_url)} title={active.title} className="w-full h-full" allowFullScreen />
              </div>
            ) : active.type === "instagram_post" && active.video_url ? (
              <div className="rounded-2xl overflow-hidden bg-white max-h-[85vh] aspect-square mx-auto max-w-lg">
                <iframe src={toEmbedUrl(active.video_url)} title={active.title} className="w-full h-full" scrolling="no" />
              </div>
            ) : (
              <img src={active.image} alt={active.title} className="rounded-2xl w-full max-h-[85vh] object-contain" />
            )}
            {active.caption && <div className="mt-3 text-white text-sm text-center">{active.caption}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
